import { type FC, useEffect, useRef } from 'react';
import { NodeType, NODE_TYPE_COLORS } from '@/types/node.types';

interface CanvasContextMenuProps {
  position: { x: number; y: number };
  onAddNode: (nodeType: NodeType, position: { x: number; y: number }) => void;
  onAddZone: (position: { x: number; y: number }) => void;
  onClose: () => void;
  /** Position in flow coordinates (for creating nodes at the right spot) */
  flowPosition: { x: number; y: number };
}

const NODE_ITEMS: { type: NodeType; label: string; icon: string }[] = [
  { type: NodeType.Vision, label: 'Vision', icon: '\u2605' },
  { type: NodeType.Objective, label: 'Objectif', icon: '\u25CE' },
  { type: NodeType.Activity, label: 'Activit\u00e9', icon: '\u26A1' },
  { type: NodeType.Risk, label: 'Risque', icon: '\u26A0' },
  { type: NodeType.Opportunity, label: 'Opportunit\u00e9', icon: '\u2728' },
  { type: NodeType.Resource, label: 'Ressource', icon: '\u2699' },
  { type: NodeType.KPI, label: 'KPI', icon: '\u{1F4CA}' },
  { type: NodeType.Stakeholder, label: 'Partie prenante', icon: '\u{1F465}' },
];

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
  padding: 'var(--spacing-xs) var(--spacing-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text)',
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  width: '100%',
  textAlign: 'left',
  transition: 'var(--transition-fast)',
  borderRadius: 'var(--radius-sm)',
};

export const CanvasContextMenu: FC<CanvasContextMenuProps> = ({
  position,
  onAddNode,
  onAddZone,
  onClose,
  flowPosition,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        zIndex: 100,
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        padding: 'var(--spacing-xs)',
        minWidth: 200,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--spacing-xs) var(--spacing-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-muted)',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Ajouter un noeud
      </div>

      {NODE_ITEMS.map((item) => (
        <button
          key={item.type}
          onClick={() => {
            onAddNode(item.type, flowPosition);
            onClose();
          }}
          style={menuItemStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-background)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <span style={{ color: NODE_TYPE_COLORS[item.type] }}>{item.icon}</span>
          <span>{item.label}</span>
        </button>
      ))}

      {/* Divider */}
      <div
        style={{
          height: 1,
          backgroundColor: 'var(--color-border)',
          margin: 'var(--spacing-xs) 0',
        }}
      />

      {/* Add zone */}
      <button
        onClick={() => {
          onAddZone(flowPosition);
          onClose();
        }}
        style={menuItemStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-background)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <span style={{ color: 'var(--color-text-muted)' }}>&#9638;</span>
        <span>Ajouter une zone</span>
      </button>

      {/* Paste placeholder */}
      <button
        disabled
        style={{
          ...menuItemStyle,
          color: 'var(--color-text-muted)',
          opacity: 0.5,
          cursor: 'default',
        }}
      >
        <span>&#128203;</span>
        <span>Coller</span>
      </button>
    </div>
  );
};

export default CanvasContextMenu;
